<script lang="ts">
  /**
   * Following
   * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/dialog_role
   */
  import { onMount, createEventDispatcher } from "svelte";

  export let title = "";
  export let body = "";

  let dialog: HTMLElement;

  const dispatcher = createEventDispatcher();

  function dismiss() {
    dispatcher("dismiss");
  }

  function handleKey(event: KeyboardEvent) {
    if (event.key === "Escape") {
      event.preventDefault();
      dismiss();
    }
  }

  function trapFocus() {
    setTimeout(() => {
      if (dialog) {
        dialog.focus();
      }
    });
  }

  function onBlur(event: FocusEvent) {
    let target = event.target as Node;

    if (dialog && dialog.contains(target) === false) {
      event.preventDefault();
      trapFocus();
    }
  }

  function onOuterClick(event: MouseEvent) {
    let target = event.target as Node;

    if (dialog.contains(target) == false) {
      dismiss();
    }
  }

  onMount(trapFocus);
</script>

<svelte:window on:keydown={handleKey} on:click={onOuterClick} />
<svelte:body on:focus|capture={onBlur} />

<div class="Container">
  <div
    class="Dialog"
    role="dialog"
    aria-labelledby="dialog-title"
    aria-describedby="dialog-body"
    bind:this={dialog}
  >
    {#if title}
      <h2 class="Title" id="dialog-title">
        {title}
      </h2>
    {/if}

    {#if body}
      <p class="Body" id="dialog-body">
        {body}
      </p>
    {/if}

    <footer class="Footer">
      <slot />
    </footer>
  </div>
</div>

<style>
  @keyframes fade-in {
    0% {
      opacity: 0;
    }

    100% {
      opacity: 100%;
    }
  }

  @keyframes pop-in {
    0% {
      opacity: 0;
      transform: translateY(10px);
    }

    100% {
      opacity: 1;
    }
  }

  .Container {
    background: rgba(0, 0, 0, 0.3);
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 24px 24px 70px;
    z-index: var(--z-overlay);
  }

  .Dialog {
    animation: 0.2s pop-in;
    background: white;
    border-radius: var(--border-radius-small);
    min-width: 280px;
    padding-top: 24px;
  }

  .Dialog:focus {
    outline: none;
    box-shadow: 0 1px 5px rgba(0, 0, 0, 0.5);
  }

  .Title {
    font-size: calc(var(--sp) * 20);
    font-weight: bold;
    padding: 0 24px 8px;
  }

  .Body {
    color: rgba(0, 0, 0, 0.78);
    font-size: calc(var(--sp) * 16);
    font-weight: 500;
    margin: 0;
    padding: 0 24px 8px;
  }

  .Footer {
    display: flex;
    justify-content: flex-end;
    padding: 8px;
  }
</style>
